import React, { useEffect, Suspense } from 'react'
import Bootstrap from 'modules/bootstrap'
import { ConnectedRouter } from 'connected-react-router'
import Notification from 'modules/notification/Notification'
import history from './history'
import { createServiceProvider } from 'services/ServiceContext'
import { Provider } from 'react-redux'
import createStore from './store'
import createContainer from 'services'
import ErrorBoundary from 'utils/ErrorBoundary'
import Routes from 'Routes'
import styles from 'App.module.sass'
import Title from './modules/title/TitleContainer'
import Login from './modules/auth/Auth'
import { getRvidFromLocalStorage } from 'modules/deviceId/DeviceIdSaga'
import TrackingService from 'services/TrackingService'
import { useFullStoryInit, FullStoryIdentityBinder } from 'utils/fullstory'
import config from 'site.config'

const Osano = React.lazy(() =>
  import('appdev-ui-library').then(module => ({
    default: module.Osano
  }))
)

const { enabled, scriptURL, colorPalette } = config.configuration.osano

const container = createContainer()
const store = createStore(history, container)
container.registerStore(store)
const ServiceProvider = createServiceProvider(container)

function trackingHandlerOsano(action, payload) {
  switch (action) {
    case 'trackClickGenericCookieBannerPolicyLink':
      TrackingService().track('Site/Click/GenericCookieBannerPolicyLink')
      break
    case 'trackClickGenericCookieBannerClose':
      TrackingService().track('Site/Click/CloseGenericCookieBanner')
      break
    case 'trackViewGenericCookieBannerView':
      TrackingService().track('Site/View/GenericCookieBanner')
      break
    case 'trackClickOsanoBannerAcceptAllBtn':
      TrackingService().track('Site/Click/AcceptAllCookies')
      break
    case 'trackClickOsanoBannerOnlyEssentialBtn':
      TrackingService().track('Site/Click/AcceptOnlyEssentialCookies')
      break
    case 'trackViewOsanoBannerShowNonUs':
      TrackingService().track('Site/View/NonUSCookieBanner')
      break
    case 'trackClickOsanoBannerCloseNonUs':
      TrackingService().track('Site/Click/CloseNonUSCookieBanner')
      break
    case 'trackClickOsanoBannerCloseNonUsB':
      TrackingService().track('Site/Click/CloseNonUSCookieBannerGroupB')
      break
    case 'trackClickOsanoBannerAcceptAllBtnB':
      TrackingService().track('Site/Click/AcceptAllCookiesGroupB')
      break
    case 'trackClickOsanoBannerCookieClickNonUs':
      TrackingService().track('Site/Click/NonUSCookieBannerPolicyLink')
      break
    case 'trackClickOsanoClickManagePreferences':
      TrackingService().track('Site/Click/NonUSCookieBanner/ManagePreference')
      break
    case 'trackViewOsanoViewManagePreferences':
      TrackingService().track('Site/View/NonUSCookieBanner/ManagePreference')
      break
    case 'trackClickOsanoDrawerClose':
      TrackingService().track('Site/Click/CloseManagePreference')
      break
    case 'trackClickOsanoDrawerTargetAdvertising':
      TrackingService().track('Site/Click/ManagePreference/TargetAdvertising')
      break
    case 'trackClickOsanoDrawerPersonalization':
      TrackingService().track('Site/Click/ManagePreference/Personalization')
      break
    case 'trackClickOsanoDrawerAnalytics':
      TrackingService().track('Site/Click/ManagePreference/Analytics')
      break
    case 'trackClickOsanoDrawerDoNotSell':
      TrackingService().track('Site/Click/ManagePreference/DoNotSell')
      break
    default:
      console.warn('Unknown tracking action:', action, payload)
  }
}

function App() {
  const rvid = getRvidFromLocalStorage()
  const country = rvid?.Country || 'us'
  const languageCode = navigator.language.split('-')[0] || 'en'

  // Effect to hide the Osano banner after duration
  const timerDuration = config.configuration.osano.bannerHideTimeout || 10
  useEffect(() => {
    const timer = setTimeout(() => {
      const dialogElement = document.querySelector('.osano-cm-dialog')
      if (dialogElement) {
        dialogElement.style.transition = 'opacity 0.5s ease'
        dialogElement.style.opacity = '0'
        setTimeout(() => {
          dialogElement.style.display = 'none'
        }, 500)
      }
    }, timerDuration * 1000)
    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useFullStoryInit()

  return (
    <Provider store={store}>
      <ServiceProvider>
        <FullStoryIdentityBinder />
        <Bootstrap>
          <ConnectedRouter className={styles.container} history={history}>
            <Title>
              <Login />
              <Routes />
            </Title>
          </ConnectedRouter>
          <Notification />
          <ErrorBoundary fallback={<p>Error on Osano component</p>}>
            <Suspense fallback={''}>
              <Osano
                enabled={enabled}
                scriptURL={scriptURL}
                country={country}
                languageCode={languageCode}
                colorPalette={colorPalette}
                trackingHandler={(action, payload) =>
                  trackingHandlerOsano(action, payload)
                }
              />
            </Suspense>
          </ErrorBoundary>
        </Bootstrap>
      </ServiceProvider>
    </Provider>
  )
}

export default App
